import React, { useState } from 'react';
import { usePortalProfile } from 'contexts/PortalProfileContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu, X, ChevronDown, ChevronRight, Zap, Sun, Moon } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import OrganizationSwitcher from './OrganizationSwitcher';
import AvailabilityControl from './AvailabilityControl';
import NotificationDropdown from 'components/Notifications/NotificationDropdown';
import { useTheme } from 'contexts/ThemeContext';
import { navPrincipal } from './navVisibility';
import { useFeatureAccess } from 'hooks/useFeatureAccess';
import { useFeatureGrants } from 'hooks/useFeatureGrants';
import { useSoftphoneEntitlement } from 'hooks/useSoftphoneEntitlement';
// The nav tree itself lives in navConfig.ts, which the mobile drawer and the
// bottom tab bar read too. This file decides how a desktop rail DRAWS it and
// nothing else — see the note at the top of navConfig.ts for why the tree is
// no longer written out here.
import { UTILITY_ITEMS, activeNavPath, buildNavZones } from './navConfig';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  // The reseller's brand and the tabs it allows. Falls back to the hostname
  // brand with no restriction when no profile applies, which is every
  // organization until one is configured.
  const { brand, isLocked } = usePortalProfile();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { user, currentOrganization, logout } = useAuth();
  const features = useFeatureAccess();
  const grants = useFeatureGrants();
  const softphone = useSoftphoneEntitlement();
  const location = useLocation();
  const navigate = useNavigate();

  // Track which sections are expanded
  // Only the two most-used Workspace sections start open; everything else is
  // collapsed so the tree is scannable on first paint.
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    agents: true,
    conversations: true,
  });

  // Who is looking: their role in the CURRENT organization, and whether that
  // organization runs the platform or runs a business on it. Both halves gate
  // every entry — see navVisibility.ts for why either alone is wrong.
  const principal = navPrincipal(currentOrganization, user, features, grants, softphone?.allowed);

  // Zones, sections and items already narrowed to this person and this
  // reseller's allowance. Identical call in MobileBottomNav — that is the
  // point: neither breakpoint does any filtering of its own.
  const visibleZones = buildNavZones(principal, isLocked);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Exactly one nav destination wins for a given URL — longest match on a real
  // path boundary. Shared with the mobile nav so a URL cannot light a different
  // entry depending on the width of the screen.
  const activePath = activeNavPath(location.pathname);

  const isPathInSection = (sectionId: string) => {
    const section = visibleZones
      .flatMap(zone => zone.sections)
      .find(sec => sec.id === sectionId);
    return !!section?.items.some(item => item.path === activePath);
  };

  return (
    <div
      className={`${isOpen ? 'w-64' : 'w-20'} h-screen transition-all duration-300 flex flex-col`}
      style={{ background: 'var(--surface)', borderRight: '1px solid var(--border)', color: 'var(--text-primary)' }}
    >
      {/* Header */}
      <div
        className="p-4 flex items-center justify-between flex-shrink-0"
        style={{ borderBottom: '1px solid var(--divider)' }}
      >
        {isOpen && (
          <div className="flex items-center gap-2.5">
            <span
              className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
              style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
            >
              <Zap className="w-4 h-4" />
            </span>
            <h2 className="text-base font-semibold tracking-tight">{brand.brandName}</h2>
          </div>
        )}
        {/* Notifications at the head, where a signal can be seen.
            It carries an unread count, so it is not an account setting to be
            filed with Settings and Help at the foot — it is the one thing in
            this rail that changes on its own and wants noticing. Beside the
            collapse control when there is room, beneath it when there is not:
            two 34px buttons do not fit side by side in a 5rem rail. */}
        <div className={`flex flex-shrink-0 gap-1 ${isOpen ? 'items-center' : 'flex-col items-center'}`}>
          <NotificationDropdown placement="side-top" />
          <button onClick={onToggle} className="tl-icon-btn flex-shrink-0" style={{ width: 34, height: 34 }}>
            {isOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {visibleZones.map((zone, zoneIdx) => (
          <div key={zone.id} className="space-y-0.5">
            {/* Zone heading — Workspace is the default context so it goes
                unlabelled; the privileged zones are named and rule-separated. */}
            {zoneIdx > 0 && (
              <div className="my-3" style={{ borderTop: '1px solid var(--divider)' }} />
            )}
            {zoneIdx > 0 && isOpen && (
              <div
                className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider select-none"
                style={{ color: 'var(--text-muted)' }}
              >
                {zone.label}
              </div>
            )}

            {/* Zone-level flat items */}
            {zone.items.map((item) => {
              const Icon = item.icon;
              const isActive = item.path === activePath;

              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`tl-nav-item ${isActive ? 'tl-nav-item-active' : ''} ${isOpen ? '' : 'justify-center'}`}
                  title={!isOpen ? item.label : undefined}
                >
                  <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                  {isOpen && <span className="flex-1">{item.label}</span>}
                </button>
              );
            })}

            {/* Zone sections */}
            {zone.sections.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded = expandedSections[section.id];
          const isSectionActive = isPathInSection(section.id);
          // Already filtered, and already carrying a 'Locked' badge where this
          // reseller's allowance stops — see filterNavItems in navConfig.ts.
          const visibleItems = section.items;

          return (
            <div key={section.id}>
              {/* Section Header */}
              <button
                onClick={() => !section.comingSoon && toggleSection(section.id)}
                disabled={section.comingSoon}
                className={`tl-nav-item ${isSectionActive && !isExpanded ? 'tl-nav-item-active' : ''} ${
                  section.comingSoon ? 'opacity-50 cursor-not-allowed' : ''
                } ${isOpen ? '' : 'justify-center'}`}
                style={{ fontWeight: 600 }}
                title={!isOpen ? section.label : undefined}
              >
                <SectionIcon className="w-[18px] h-[18px] flex-shrink-0" />
                {isOpen && (
                  <>
                    <span className="flex-1 text-left">{section.label}</span>
                    {section.comingSoon ? (
                      <span className="tl-pill tl-pill-amber">Soon</span>
                    ) : (
                      isExpanded ?
                        <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-60" /> :
                        <ChevronRight className="w-4 h-4 flex-shrink-0 opacity-60" />
                    )}
                  </>
                )}
              </button>

              {/* Section Items */}
              {isExpanded && !section.comingSoon && isOpen && (
                <div className="ml-5 mt-1 space-y-0.5 pl-2" style={{ borderLeft: '1px solid var(--divider)' }}>
                  {visibleItems.map((item, idx) => {
                    const ItemIcon = item.icon;
                    const isActive = item.path === activePath;
                    // Render a small sub-group header the first time a new
                    // group label appears (de-clutters long sections).
                    const showGroupHeader = !!item.group && item.group !== visibleItems[idx - 1]?.group;

                    return (
                      <React.Fragment key={item.path}>
                        {showGroupHeader && (
                          <div
                            className={`px-3 ${idx === 0 ? 'pt-0.5' : 'pt-2'} pb-0.5 text-[10px] font-semibold uppercase tracking-wider select-none`}
                            style={{ color: 'var(--text-muted)' }}
                          >
                            {item.group}
                          </div>
                        )}
                        <button
                          onClick={() => !item.badge && navigate(item.path)}
                          disabled={!!item.badge}
                          className={`tl-nav-item ${isActive ? 'tl-nav-item-active' : ''} ${
                            item.badge ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          style={{ fontSize: 13, padding: '7px 12px' }}
                        >
                          <ItemIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="flex-1 text-left">{item.label}</span>
                          {item.badge && (
                            <span className="tl-pill tl-pill-amber">{item.badge}</span>
                          )}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* Collapsed state - show items as icons */}
              {isExpanded && !section.comingSoon && !isOpen && (
                <div className="mt-1 space-y-1">
                  {visibleItems.map((item) => {
                    const ItemIcon = item.icon;
                    const isActive = item.path === activePath;

                    return (
                      <button
                        key={item.path}
                        onClick={() => !item.badge && navigate(item.path)}
                        disabled={!!item.badge}
                        className={`tl-nav-item justify-center ${isActive ? 'tl-nav-item-active' : ''} ${
                          item.badge ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title={item.label}
                      >
                        <ItemIcon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 space-y-3 flex-shrink-0" style={{ borderTop: '1px solid var(--divider)' }}>
        {/* Organization Switcher */}
        <OrganizationSwitcher isExpanded={isOpen} />

        {/* User Info */}
        {/* Availability sits ON this card, under the role.

            The sidebar already sorts these by kind: notifications are a signal
            and live at the head, the theme is a once-a-year preference and
            lives in the footer. Availability is neither — it changes several
            times a day, has to be readable without hunting for it, and is a
            statement about YOU, which is what this card is for.

            It renders nothing unless the organisation has turned conversation
            routing on, so no existing tenant gains a control for a queue that
            does not exist. */}
        <div
          className={`flex items-center gap-2.5 ${isOpen ? 'p-2' : 'justify-center py-2'}`}
          style={isOpen ? {
            borderRadius: 12,
            border: '1px solid var(--border)',
            background: 'var(--surface-raised)',
          } : undefined}
        >
          <div className="relative flex-shrink-0">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
              style={{ background: 'var(--coral-tint)', color: 'var(--coral-label)' }}
            >
              {user?.firstName?.charAt(0).toUpperCase() || user?.name?.charAt(0).toUpperCase() || '?'}
            </div>
            {/* Collapsed, the status rides the avatar as a badge rather than
                sitting beside it: a 4rem rail has no width to give away, and
                the badge is the pattern people already read as presence. */}
            {!isOpen && (
              <span className="absolute -bottom-0.5 -right-0.5 rounded-full p-[2px]"
                    style={{ background: 'var(--surface-raised, #fff)' }}>
                <AvailabilityControl collapsed />
              </span>
            )}
          </div>
          {isOpen && (
            <div className="flex-1 overflow-hidden leading-tight">
              <p className="text-[12.5px] font-semibold truncate">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.name || 'User'}
              </p>
              <p className="text-[11px] capitalize truncate" style={{ color: 'var(--text-muted)' }}>
                {(currentOrganization?.role || user?.role)?.replace('_', ' ') || 'No role'}
              </p>
              <AvailabilityControl />
            </div>
          )}
        </div>

        {/* Footer Navigation — the account's own destinations, pinned below the
            zones rather than inside one. Same two entries the drawer lists. */}
        {/* One list, one shape.
            The theme lives here as a nav row rather than as an icon wedged
            into the account card above: a card about who you are is not a home
            for controls, and at 16rem wide it pushed the name into an ellipsis.
            It is a preference set about once a year, so the foot of the list,
            beside Settings, is exactly where it belongs.

            Notifications deliberately does NOT live here. It carries an unread
            count, which makes it a signal rather than a setting, and a signal
            buried at the bottom of the account utilities is one nobody sees. It
            sits at the head of the sidebar instead. */}
        <div className="space-y-0.5">
          {UTILITY_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`tl-nav-item ${location.pathname === item.path ? 'tl-nav-item-active' : ''} ${
                  isOpen ? '' : 'justify-center'
                }`}
                title={!isOpen ? item.label : undefined}
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                {isOpen && <span className="flex-1">{item.label}</span>}
              </button>
            );
          })}

          <button
            onClick={toggleTheme}
            className={`tl-nav-item ${isOpen ? '' : 'justify-center'}`}
            title={!isOpen ? (resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}
          >
            {resolvedTheme === 'dark'
              ? <Sun className="w-[18px] h-[18px] flex-shrink-0" />
              : <Moon className="w-[18px] h-[18px] flex-shrink-0" />}
            {isOpen && <span className="flex-1">{resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
          </button>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="tl-btn tl-btn-danger tl-btn-sm w-full"
          title={!isOpen ? 'Logout' : undefined}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {isOpen && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
